import React, { useState } from 'react';
import {
  UserIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';

interface UserPath {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  steps: {
    id: string;
    title: string;
    description: string;
    tabKey: string;
    completed?: boolean;
  }[];
  benefits: string[];
}

interface UserPathGuideProps {
  onSelectPath: (path: UserPath) => void;
  onNavigateToStep: (tabKey: string) => void;
  currentTab: string;
}

const UserPathGuide: React.FC<UserPathGuideProps> = ({ onSelectPath, onNavigateToStep, currentTab }) => {
  const [selectedPath, setSelectedPath] = useState<UserPath | null>(null);
  const [showGuide, setShowGuide] = useState(true);

  const userPaths: UserPath[] = [
    {
      id: 'creator',
      title: '🎵 创作者路径',
      description: '音乐创作者，想要保护版权并获得收益',
      icon: UserIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      steps: [
        {
          id: 'wallet',
          title: '连接钱包',
          description: '连接您的数字钱包，开始区块链之旅',
          tabKey: 'wallet'
        },
        {
          id: 'copyright',
          title: '版权管理',
          description: '将您的音乐作品版权上链保护',
          tabKey: 'copyright'
        },
        {
          id: 'works',
          title: '作品展示',
          description: '展示您的NFT音乐作品，吸引投资者',
          tabKey: 'works'
        },
        {
          id: 'analytics',
          title: '收益分析',
          description: '查看作品收益和投资回报数据',
          tabKey: 'analytics'
        }
      ],
      benefits: [
        '🔒 永久版权保护',
        '💰 持续版税收入',
        '📈 作品价值增长',
        '🤝 透明收益分配'
      ]
    },
    {
      id: 'investor',
      title: '💎 投资者路径',
      description: '音乐投资者，寻找有潜力的音乐NFT',
      icon: CurrencyDollarIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      steps: [
        {
          id: 'wallet',
          title: '连接钱包',
          description: '连接钱包，准备投资资金',
          tabKey: 'wallet'
        },
        {
          id: 'nft',
          title: 'NFT市场',
          description: '浏览和购买音乐NFT作品',
          tabKey: 'nft'
        },
        {
          id: 'analytics-investment',
          title: '投资价值分析',
          description: '查看收益分析-NFT投资价值',
          tabKey: 'analytics'
        },
        {
          id: 'auction',
          title: '参与拍卖',
          description: '在拍卖中竞拍稀有音乐NFT',
          tabKey: 'auction'
        }
      ],
      benefits: [
        '🚀 早期投资机会',
        '📊 数据驱动决策',
        '💎 稀有NFT收藏',
        '🎯 高收益潜力'
      ]
    },
    {
      id: 'collaborator',
      title: '🤝 协作者路径',
      description: '音乐协作者，参与作品创作和收益分配',
      icon: UsersIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      steps: [
        {
          id: 'copyright-collab',
          title: '版权管理',
          description: '管理协作作品的版权分配',
          tabKey: 'copyright'
        },
        {
          id: 'contracts',
          title: '智能合约',
          description: '设置自动化的收益分配合约',
          tabKey: 'contracts'
        },
        {
          id: 'analytics-revenue',
          title: '收益分配',
          description: '查看协作收益和分成明细',
          tabKey: 'analytics'
        },
        {
          id: 'dao',
          title: 'DAO治理',
          description: '参与社区决策和治理投票',
          tabKey: 'dao'
        }
      ],
      benefits: [
        '⚖️ 公平收益分配',
        '🔄 自动化结算',
        '🗳️ 参与决策权',
        '📋 透明合约条款'
      ]
    }
  ];

  const getCurrentStepIndex = (path: UserPath) => {
    return path.steps.findIndex(step => step.tabKey === currentTab);
  };

  const getCompletedSteps = (path: UserPath) => {
    const currentIndex = getCurrentStepIndex(path);
    return path.steps.map((step, index) => ({
      ...step,
      completed: index < currentIndex
    }));
  };

  if (!showGuide) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border">
      {!selectedPath ? (
        // 路径选择界面
        <div className="p-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🎯 选择您的使用路径</h2>
            <p className="text-gray-600">根据您的角色，我们为您推荐最佳的功能使用流程</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {userPaths.map((path) => (
              <div
                key={path.id}
                className={`${path.bgColor} rounded-lg p-6 cursor-pointer transition-all hover:shadow-md border-2 border-transparent hover:border-gray-200`}
                onClick={() => {
                  setSelectedPath(path);
                  onSelectPath(path);
                }}
              >
                <div className="text-center mb-4">
                  <div className={`w-16 h-16 ${path.bgColor} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <path.icon className={`w-8 h-8 ${path.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{path.title}</h3>
                  <p className="text-sm text-gray-600">{path.description}</p>
                </div>

                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-medium text-gray-700">核心优势:</h4>
                  {path.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs text-gray-600">
                      <StarIcon className="w-3 h-3 text-yellow-500" />
                      {benefit}
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <button className={`inline-flex items-center gap-2 px-4 py-2 ${path.color} bg-white rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors`}>
                    <PlayCircleIcon className="w-4 h-4" />
                    开始使用
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setShowGuide(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              跳过引导，直接使用
            </button>
          </div>
        </div>
      ) : (
        // 步骤引导界面
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <selectedPath.icon className={`w-6 h-6 ${selectedPath.color}`} />
              <h3 className="text-lg font-semibold text-gray-900">{selectedPath.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedPath(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                切换路径
              </button>
              <button
                onClick={() => setShowGuide(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                隐藏引导
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {getCompletedSteps(selectedPath).map((step, index) => {
              const isActive = step.tabKey === currentTab;
              const isCompleted = step.completed;
              const isNext = !isCompleted && !isActive && index === getCurrentStepIndex(selectedPath) + 1;

              return (
                <div
                  key={step.id}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isActive
                      ? `${selectedPath.bgColor} border-${selectedPath.color.split('-')[1]}-300`
                      : isCompleted
                      ? 'bg-green-50 border-green-300'
                      : isNext
                      ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  onClick={() => onNavigateToStep(step.tabKey)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive
                        ? selectedPath.bgColor
                        : isCompleted
                        ? 'bg-green-500'
                        : isNext
                        ? 'bg-yellow-500'
                        : 'bg-gray-300'
                    }`}>
                      {isCompleted ? (
                        <CheckCircleIcon className="w-5 h-5 text-white" />
                      ) : (
                        <span className={`text-sm font-bold ${
                          isActive ? selectedPath.color : 'text-white'
                        }`}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-medium ${
                        isActive ? selectedPath.color : isCompleted ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        {step.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                      {isNext && (
                        <div className="mt-2">
                          <span className="inline-flex items-center gap-1 text-xs text-yellow-700 font-medium">
                            <ArrowRightIcon className="w-3 h-3" />
                            下一步
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              当前进度: {getCurrentStepIndex(selectedPath) + 1} / {selectedPath.steps.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPathGuide;
